/**
 * `<CodexApp>` — the **CODEX** page: the campaign codex's one served payload, with the roster's
 * lancers first, rendered by {@link RolodexApp}. The rail head carries the page's name; there
 * is no tab strip or second header (`decisions/0205` point 2, F2).
 *
 * **`spineOpen`** is the one piece of local state: whether the index's `<860px` overlay is
 * open. Its toggle (`☰ INDEX`) sits in the reader's head. Purely ephemeral, not app state.
 *
 * ── WHY `.gms-content`'S HEIGHT IS MEASURED, NOT `height: 100%`ED ──
 *
 * The codex root (`.rlx-shell`) needs a REAL, definite height so it — not `.gms-content` — can
 * own scrolling below it. `height: 100%` does NOT work here: `.gms-content` carries only
 * `max-height`, never an explicit `height`, and CSS only resolves a percentage `height` against
 * an ancestor whose own `height` computes to a length. So this component measures
 * `.gms-content` with one `ResizeObserver` and threads the result down as `hostHeight`, applied
 * as a real inline `height` on the pane's root; everything below that is ordinary flexbox.
 */
import { useLayoutEffect, useRef, useState } from "react";
import type { CodexContent } from "@lancer-vtt/domain";
import { RolodexApp, type BackdropPin, type CodexViewer, type LancerActions } from "../rolodex/RolodexApp";
import type { Lancer } from "../rolodex/lancers";

export function CodexApp({
  content,
  lancers,
  viewer,
  actions,
  backdrop,
  viewedEntryId,
  onSelectEntry,
  onBack,
  maxHeight,
}: {
  /** The authored codex, fetched once over `WORLD_CODEX_PATH`. An empty entry list is legal. */
  content: CodexContent;
  /** The roster's lancers (`useLancers`). */
  lancers: readonly Lancer[];
  viewer: CodexViewer;
  actions: LancerActions;
  /** The GM's narrative-backdrop pin; absent for a player. */
  backdrop?: BackdropPin;
  /** The open file's id (`/codex/rolodex/<id>`), or `ROLODEX_LIST`. */
  viewedEntryId: string;
  /** Open a file. */
  onSelectEntry: (entryId: string) => void;
  /** Close the open file, back to the brief. */
  onBack: () => void;
  /** The page's live content-host cap — same contract as every other `.gms-content` host. */
  maxHeight: number;
}) {
  const [spineOpen, setSpineOpen] = useState(false);

  // ── `.gms-content`'s measured height — see the module doc. `maxHeight` is the initial guess
  // before the observer's first callback.
  const contentRef = useRef<HTMLDivElement>(null);
  const [hostHeight, setHostHeight] = useState(maxHeight);
  useLayoutEffect(() => {
    const el = contentRef.current;
    if (!el) return;
    const ro = new ResizeObserver((entries) => {
      const r = entries[0]?.contentRect;
      if (r) setHostHeight(Math.floor(r.height));
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // Opening a file closes an open index overlay — there's nothing left to browse for. A no-op
  // at `>=860px`, where the overlay class is inert.
  const handleSelectEntry = (entryId: string) => {
    onSelectEntry(entryId);
    setSpineOpen(false);
  };

  return (
    <div className="gms-content" style={{ maxHeight }} ref={contentRef}>
      <RolodexApp
        content={content}
        lancers={lancers}
        viewer={viewer}
        actions={actions}
        backdrop={backdrop}
        viewedEntryId={viewedEntryId}
        onSelect={handleSelectEntry}
        onBack={onBack}
        spineOpen={spineOpen}
        onSpineToggle={() => setSpineOpen((o) => !o)}
        onSpineClose={() => setSpineOpen(false)}
        hostHeight={hostHeight}
      />
    </div>
  );
}
